import { UUISelectTheme } from '../../../types/UUISelect'

const selectTheme: UUISelectTheme = {
  trigger: ({ /* empty, */ disabled, /* opened, */ size, style }) => {
    const baseClassNames = [
      'tw:font-ubuntu tw:font-normal tw:[&_span]:box-border',
      'tw:w-full tw:flex tw:items-center tw:justify-center',
      disabled ? 'tw:cursor-default' : 'tw:cursor-pointer',
      'tw:[&_[data-uuiselect-trigger-icon]]:flex-[0_0_16px] tw:[&_[data-uuiselect-trigger-icon]]:h-[16px] tw:[&_[data-uuiselect-trigger-icon]]:text-aquamarine-400'
    ]

    const sizeClassNames = []
    switch (size) {
      case 'small': {
        sizeClassNames.push(
          'tw:rounded-[20px] tw:text-[12px] tw:h-[24px] tw:py-[4px] tw:pr-[12px] tw:pl-[16px] tw:leading-[16px] tw:gap-[12px]'
        )
        break
      }
      case 'large': {
        sizeClassNames.push(
          'tw:rounded-[20px] tw:text-[16px] tw:h-[40px] tw:py-[6px] tw:pr-[12px] tw:pl-[16px] tw:leading-[24px] tw:gap-[12px]'
        )
        break
      }
      case 'normal':
      default: {
        sizeClassNames.push(
          'tw:rounded-[20px] tw:text-[14px] tw:h-[32px] tw:py-[6px] tw:pr-[12px] tw:pl-[16px] tw:leading-[20px] tw:gap-[12px]'
        )
        break
      }
    }

    const styleClassNames = []
    if (disabled) {
      styleClassNames.push('tw:bg-grey-800 tw:text-grey-600 tw:[&_[data-uuiselect-trigger-icon]]:text-grey-600')
    } else {
      switch (style) {
        case 'lowered': {
          styleClassNames.push(
            'tw:text-primary-text tw:bg-grey-1000',
            // hover
            'tw:hover:text-aquamarine-400 tw:hover:bg-grey-700 tw:hover:[&_[data-uuiselect-trigger-icon]]:text-aquamarine-400',
            // focus
            'tw:focus-visible:text-blue-100 tw:focus-visible:bg-grey-1000 tw:focus-visible:[&_[data-uuiselect-trigger-icon]]:text-blue-100',
            // focus hover
            'tw:focus-visible:hover:text-blue-100 tw:focus-visible:hover:bg-grey-700 tw:focus-visible:hover:[&_[data-uuiselect-trigger-icon]]:text-blue-100'
          )
          break
        }
        case 'raised':
        default: {
          styleClassNames.push(
            'tw:text-primary-text tw:bg-aquamarine-10',
            // hover
            'tw:hover:text-aquamarine-400 tw:hover:bg-aquamarine-25 tw:hover:[&_[data-uuiselect-trigger-icon]]:text-aquamarine-400',
            // focus
            'tw:focus-visible:text-blue-100 tw:focus-visible:bg-aquamarine-10 tw:focus-visible:[&_[data-uuiselect-trigger-icon]]:text-blue-100',
            // focus hover
            'tw:focus-visible:hover:text-blue-100 tw:focus-visible:hover:bg-aquamarine-25 tw:focus-visible:hover:[&_[data-uuiselect-trigger-icon]]:text-blue-100'
          )
          break
        }
      }
    }

    return [baseClassNames.join(' '), sizeClassNames.join(' '), styleClassNames.join(' ')].join(' ')
  },
  options: (/*{ size, style }*/) => {
    const baseClassNames = [
      'tw:font-ubuntu tw:font-normal tw:z-1000 tw:overflow-hidden tw:flex tw:flex-col tw:justify-center tw:items-start',
      'tw:rounded-[20px] tw:[box-shadow:0_0_15px_4px_rgba(0,0,0,0.05)] tw:bg-grey-1000',
      'tw:[width:var(--uui-select-button-width)] tw:[max-height:var(--uui-select-options-available-height)]'
    ]
    return baseClassNames.join(' ')
  },
  option: ({ selected, disabled, size /*, style */ }) => {
    const baseClassNames = [
      'tw:text-left tw:w-full tw:font-ubuntu tw:font-normal tw:box-border tw:whitespace-normal tw:break-words tw:break-normal   tw:wrap-anywhere',
      disabled ? 'tw:text-grey-600 tw:cursor-default' : 'tw:text-primary-text  tw:cursor-pointer',
      selected ? 'tw:bg-aquamarine-50' : 'tw:bg-transparent',
      'tw:data-[highlighted]:bg-grey-800'
    ]
    const sizeClassNames = []
    switch (size) {
      case 'small': {
        sizeClassNames.push('tw:py-[4px] tw:pr-[12px] tw:pl-[16px] tw:text-[12px] tw:leading-[16px]')
        break
      }
      case 'large': {
        sizeClassNames.push('tw:py-[8px] tw:pr-[12px] tw:pl-[16px] tw:text-[16px] tw:leading-[24px]')
        break
      }
      case 'normal':
      default: {
        sizeClassNames.push('tw:py-[6px] tw:pr-[12px] tw:pl-[16px] tw:text-[14px] tw:leading-[20px]')
        break
      }
    }
    return [baseClassNames.join(' '), sizeClassNames.join(' ')].join(' ')
  },
  groupLabel: ({ size /* , style */ }) => {
    const baseClassNames = ['tw:text-left tw:font-bold']
    const sizeClassNames = []
    switch (size) {
      case 'small': {
        sizeClassNames.push('tw:pt-[12px] tw:pb-[4px] tw:pr-[12px] tw:pl-[14px] tw:text-[12px] tw:leading-[16px]')
        break
      }
      case 'large': {
        sizeClassNames.push('tw:pt-[24px] tw:pb-[8px] tw:pr-[12px] tw:pl-[14px] tw:text-[16px] tw:leading-[24px]')
        break
      }
      case 'normal':
      default: {
        sizeClassNames.push('tw:pt-[18px] tw:pb-[6px] tw:pr-[12px] tw:pl-[14px] tw:text-[14px] tw:leading-[20px]')
        break
      }
    }
    return [baseClassNames.join(' '), sizeClassNames.join(' ')].join(' ')
  },
  filter: ({ size /* , style */ }) => {
    const baseClassName = [
      'tw:flex tw:[width:var(--uui-select-button-width)] tw:gap-[8px] tw:box-border tw:border-b tw:border-grey-800',
      'tw:[&_[data-uuiselect-filter-input]]:flex-1 tw:[&_[data-uuiselect-filter-input]]:min-w-0 tw:[&_[data-uuiselect-filter-input]]:border-0',
      'tw:[&_[data-uuiselect-filter-input]]:text-primary-text tw:[&_[data-uuiselect-filter-input]]:placeholder-grey-500',
      'tw:[&_[data-uuiselect-filter-icon]]:text-primary-text tw:[&_.icon-aytmfd-delete]:cursor-pointer  tw:[&_.icon-aytmfd-delete]:text-grey-600',
      'tw:[&_.icon-aytmfd-delete]:hover:text-grey-300'
    ]
    const sizeClassNames = []
    switch (size) {
      case 'small': {
        sizeClassNames.push(
          'tw:h-[24px] tw:py-[4px] tw:pr-[6px] tw:pl-[16px]',
          'tw:[&_[data-uuiselect-filter-input]]:text-[12px] tw:[&_[data-uuiselect-filter-input]]:leading-[16px]',
          'tw:[&_[data-uuiselect-filter-icon]]:flex-[0_0_16px] tw:[&_[data-uuiselect-filter-icon]]:w-[16px] tw:[&_[data-uuiselect-filter-icon]]:h-[16px]',
          'tw:[&_.icon-aytmfd-delete]:text-[22px] tw:[&_.icon-aytmfd-delete]:leading-[16px] tw:[&_.icon-aytmfd-delete]:relative tw:[&_.icon-aytmfd-delete]:left-[-3px] tw:[&_.icon-aytmfd-delete]:top-[-2px]',
          'tw:[&_.icon-aytmfd-search]:text-[12px] tw:[&_.icon-aytmfd-search]:leading-[16px]'
        )
        break
      }
      case 'large': {
        sizeClassNames.push(
          'tw:h-[40px] tw:py-[8px] tw:pr-[6px] tw:pl-[16px]',
          'tw:[&_[data-uuiselect-filter-input]]:text-[16px] tw:[&_[data-uuiselect-filter-input]]:leading-[24px]',
          'tw:[&_[data-uuiselect-filter-icon]]:flex-[0_0_16px] tw:[&_[data-uuiselect-filter-icon]]:w-[16px] tw:[&_[data-uuiselect-filter-icon]]:h-[24px]',
          'tw:[&_.icon-aytmfd-delete]:text-[24px] tw:[&_.icon-aytmfd-delete]:leading-[24px] tw:[&_.icon-aytmfd-delete]:relative tw:[&_.icon-aytmfd-delete]:left-[-4px] tw:[&_.icon-aytmfd-delete]:top-[1px]',
          'tw:[&_.icon-aytmfd-search]:text-[16px] tw:[&_.icon-aytmfd-search]:leading-[24px]'
        )
        break
      }
      case 'normal':
      default: {
        sizeClassNames.push(
          'tw:h-[32px] tw:py-[6px] tw:pr-[6px] tw:pl-[16px]',
          'tw:[&_[data-uuiselect-filter-input]]:text-[14px] tw:[&_[data-uuiselect-filter-input]]:leading-[20px]',
          'tw:[&_[data-uuiselect-filter-icon]]:flex-[0_0_16px] tw:[&_[data-uuiselect-filter-icon]]:w-[16px] tw:[&_[data-uuiselect-filter-icon]]:h-[20px]',
          'tw:[&_.icon-aytmfd-delete]:text-[24px] tw:[&_.icon-aytmfd-delete]:leading-[20px] tw:[&_.icon-aytmfd-delete]:relative tw:[&_.icon-aytmfd-delete]:left-[-4px] tw:[&_.icon-aytmfd-delete]:top-[-1px]',
          'tw:[&_.icon-aytmfd-search]:text-[16px] tw:[&_.icon-aytmfd-search]:leading-[20px]'
        )
        break
      }
    }
    return [baseClassName.join(' '), sizeClassNames.join(' ')].join(' ')
  },
  invalid: () => 'tw:border-red-50 tw:border'
}

export default selectTheme
